import type { DivisionConfig, PopulatedFlight } from "./flightPlacementTypes";

/**
 * One registration's division/flight assignment derived from its position in populatedFlights.
 */
export interface RegistrationFlightAssignment {
    _id: string;
    division: string;
    divisionName: string;
    flight: string;
    flightName: string;
}

/**
 * Derives division and flight assignment for every player in populatedFlights.
 * Used when saving after the user has moved players between flights in the preview:
 * we persist these assignments back onto the registration records so division/flight
 * stay in sync with the drag-and-drop layout.
 *
 * LOGIC:
 * - For each flight in populatedFlights we know its division via divisionIndex and
 *   its flight name from flightName.
 * - When useDivisionsAsFlights is true, there is no separate "flight" — the division
 *   acts as the flight, so we set both division and flight to the division name.
 * - When useDivisionsAsFlights is false, we set division from the division config
 *   and flight from the flight's flightName.
 * - Each player in flight.players gets an assignment entry keyed by _id.
 */
export function getRegistrationFlightAssignments(
    populatedFlights: PopulatedFlight[],
    divisions: DivisionConfig[],
    useDivisionsAsFlights: boolean
): RegistrationFlightAssignment[] {
    const assignments: RegistrationFlightAssignment[] = [];

    populatedFlights.forEach((flight: PopulatedFlight) => {
        const division = divisions[flight.divisionIndex];
        const divisionName = (division?.divisionName ?? "").toString();
        const flightName = useDivisionsAsFlights
            ? divisionName
            : (flight.flightName ?? "").toString();

        const players = Array.isArray(flight.players) ? flight.players : [];
        players.forEach((player: unknown) => {
            const _id = (player as { _id?: string })._id;
            if (!_id) return;
            assignments.push({
                _id,
                division: divisionName,
                divisionName,
                flight: flightName,
                flightName,
            });
        });
    });

    return assignments;
}
